import { Link } from 'react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import type { TrashListResponse } from '@ousse/shared'
import { api } from '../lib/api'
import { useHousehold } from '../lib/household'
import { Alert, ErrorAlert } from '../components/ui'
import { useState } from 'react'

/** Corbeille du foyer : restauration d'un rangement (sous-arbre) ou d'un objet. */
export function TrashPage() {
  const { t, i18n } = useTranslation()
  const { current } = useHousehold()
  const qc = useQueryClient()
  const [error, setError] = useState<unknown>(null)
  const canWrite = current && current.role !== 'viewer' && !current.readOnly

  const trash = useQuery({
    queryKey: ['trash', current?.id],
    enabled: Boolean(current?.id),
    queryFn: () => api.get<TrashListResponse>(`/households/${current!.id}/trash`),
  })

  const restore = useMutation({
    mutationFn: async (entry: { kind: 'location' | 'item'; id: string }) => {
      if (entry.kind === 'location') {
        await api.post(`/households/${current!.id}/trash/locations/${entry.id}/restore`, {})
      } else {
        await api.post(`/households/${current!.id}/trash/items/${entry.id}/restore`, {})
      }
    },
    onSuccess: async () => {
      setError(null)
      await qc.invalidateQueries({ queryKey: ['trash'] })
      await qc.invalidateQueries({ queryKey: ['contents'] })
      await qc.invalidateQueries({ queryKey: ['items'] })
      await qc.invalidateQueries({ queryKey: ['activity'] })
    },
    onError: setError,
  })

  if (!current) return <p role="status">{t('common.loading')}</p>

  const items = trash.data?.items ?? []
  const days = trash.data?.retentionDays ?? 30

  return (
    <>
      <h1>
        <Trash2 aria-hidden size={28} style={{ verticalAlign: 'middle', marginRight: '0.5rem' }} />
        {t('trash.title')}
      </h1>
      <p className="muted">{t('trash.hint', { days })}</p>
      {!canWrite && <Alert kind="warning">{t('inventory.readOnlyHint')}</Alert>}
      <ErrorAlert error={error} />
      {trash.isLoading && <p role="status">{t('common.loading')}</p>}
      {!trash.isLoading && items.length === 0 && <p className="muted">{t('trash.empty')}</p>}
      {items.length > 0 && (
        <ul className="list">
          {items.map((entry) => {
            const label =
              entry.kind === 'location'
                ? t('trash.kindLocation', {
                    name: entry.name,
                    count: entry.subtreeCount ?? 1,
                  })
                : t('trash.kindItem', { name: entry.name })
            const when = new Date(entry.deletedAt).toLocaleString(i18n.language)
            return (
              <li key={`${entry.kind}-${entry.id}`}>
                <span style={{ flex: 1 }}>
                  <strong>{label}</strong>
                  <br />
                  <span className="muted">{t('trash.deletedAt', { when })}</span>
                </span>
                {canWrite && (
                  <button
                    type="button"
                    className="btn"
                    disabled={restore.isPending}
                    onClick={() => restore.mutate({ kind: entry.kind, id: entry.id })}
                  >
                    {t('trash.restore')}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}
      <p>
        <Link className="btn secondary" to="/storage">
          {t('inventory.seeStorage')}
        </Link>
      </p>
    </>
  )
}
